import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, CheckCircle, ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";

import SEO from "../components/SEO";
import { serviceSchema } from "../seo/pageSchema";
import { getServiceBySlug, getCompanyInfo } from "../services/api";

const ServicePage = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({
    phone: "1-888-907-3777"
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [serviceData, companyInfoData] = await Promise.all([
          getServiceBySlug(slug),
          getCompanyInfo()
        ]);

        setService(serviceData);
        setCompanyInfo(companyInfoData);
      } catch (error) {
        console.error("Error fetching service:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div>
        <SEO
          title="Service Not Found | Groundhogs Septics"
          robots="noindex, follow"
          description="The service you are looking for could not be found. View all septic installation services available in Thunder Bay and Northwestern Ontario."
        />
        <Header />
        <div className="pt-32 pb-16 text-center">
          <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
          <Link
            to="/services/septic-system-installation-thunder-bay"
            className="text-orange-600 hover:underline"
          >
            View Septic Installation Services
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const isTankInstallReplacement = slug === "septic-tank-installation-thunder-bay";
  const isSystemInstall = slug === "septic-system-installation-thunder-bay";

  const displayTitle = isTankInstallReplacement
    ? "Septic Tank Installation & Replacement in Thunder Bay"
    : service.title;

  const seoTitle = isTankInstallReplacement
    ? "Septic Tank Installation & Replacement in Thunder Bay | Groundhogs™ Septic Systems"
    : isSystemInstall
      ? "Septic System Installation Thunder Bay | Groundhogs™ Septic Systems"
      : `${service.title.substring(0, 45)} | Thunder Bay`;

  const seoDescription = isTankInstallReplacement
    ? "Septic tank installation and septic tank replacement in Thunder Bay and Northwestern Ontario. Concrete, fiberglass, and poly tank options. Permits, removal, installation, and reconnect to existing drain field (if viable)."
    : isSystemInstall
      ? "Septic system installation in Thunder Bay and Northwestern Ontario (Shuniah, Rosslyn, Gorham, Nipigon, Oliver Paipoonge, Murillo). Traditional systems, Eljen GSF, and holding tanks. 1,200+ installs. Licensed & insured. Permits and OBC-compliant installs."
      : service.description.substring(0, 150);

  const seoKeywords = isTankInstallReplacement
    ? "septic tank replacement Thunder Bay, septic tank installation Thunder Bay, replace septic tank, septic tank installers near me, septic tank install Thunder Bay, septic tank replacement cost Ontario"
    : isSystemInstall
      ? "septic installation Thunder Bay, septic system installation Thunder Bay, septic installers near me, septic tanks Thunder Bay, Eljen GSF installation Thunder Bay, holding tank installation Thunder Bay"
      : `${service.title} Thunder Bay, septic installation`;

  const tankFaq = [
    {
      question: "Do you do septic tank installation and replacement in Thunder Bay?",
      answer:
        "Yes. Groundhogs provides septic tank installation for new builds and septic tank replacement when an existing tank is cracked, undersized, leaking, or non-compliant. We serve Thunder Bay and surrounding Northwestern Ontario communities.",
    },
    {
      question: "Can you replace just the tank and keep the existing septic field?",
      answer:
        "Sometimes. If the drain field is still functioning and meets site constraints, we may be able to reconnect a new tank to the existing field. We assess the field and provide a clear recommendation before work begins.",
    },
    {
      question: "Do you handle permits and inspections?",
      answer:
        "Yes. We coordinate required permits and inspections and ensure the installation is compliant with Ontario Building Code requirements and local health unit processes.",
    },
    {
      question: "What septic tank types do you install?",
      answer:
        "We install concrete and fiberglass tanks, and we can also supply and install poly/plastic tank options (including Roth MultiTank) where appropriate for the project and code requirements.",
    },
    {
      question: "How long does a septic tank replacement take?",
      answer:
        "Many tank replacements can be completed in about 3 days on average depending on access, soil conditions, and weather. We’ll confirm timelines after a site review.",
    },
  ];

  const systemInstallFaq = [
    {
      question: "Do you install septic systems in Thunder Bay and surrounding areas?",
      answer:
        "Yes. We install septic systems in Thunder Bay and surrounding communities including Shuniah, Rosslyn, Gorham, Nipigon, Oliver Paipoonge, and Murillo. Each design is based on your site conditions and Ontario Building Code requirements.",
    },
    {
      question: "What septic system types do you install?",
      answer:
        "We install traditional leaching bed systems, advanced Eljen GSF systems for challenging soils, and holding tanks where appropriate. The right option depends on soil, bedrock, groundwater, and available space.",
    },
    {
      question: "Do you handle permits and inspections for a new septic installation?",
      answer:
        "Yes. We coordinate the required permits/inspections and ensure the installation meets Ontario Building Code requirements and local authority processes.",
    },
    {
      question: "Do you do percolation tests or site assessments?",
      answer:
        "We assess soil, slope, groundwater, and layout constraints to determine the best system design. Where required, percolation testing and engineered design steps are coordinated as part of the project planning.",
    },
    {
      question: "Can you supply and install Roth MultiTank for septic or water storage?",
      answer:
        "Yes. We can supply (and install when appropriate) Roth MultiTank solutions for septic and water storage applications in Northwestern Ontario.",
    },
  ];

  const structuredData = isTankInstallReplacement
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            name: "Septic Tank Installation & Replacement in Thunder Bay",
            description:
              "Septic tank installation and replacement in Thunder Bay and Northwestern Ontario, including permits, excavation, tank supply, installation, and reconnecting to an existing drain field (if viable).",
            areaServed: {
              "@type": "AdministrativeArea",
              name: "Thunder Bay & Northwestern Ontario",
            },
            provider: {
              "@type": "LocalBusiness",
              name: "Groundhogs Septic Systems",
              url: "https://groundhogsseptics.com/",
            },
            serviceType: [
              "Septic tank installation",
              "Septic tank replacement",
              "Tank riser installation",
              "Permit coordination",
            ],
          },
          {
            "@type": "FAQPage",
            mainEntity: tankFaq.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.answer,
              },
            })),
          },
        ],
      }
    : isSystemInstall
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: "Septic System Installation in Thunder Bay & Northwestern Ontario",
              description:
                "Septic system installation in Thunder Bay and Northwestern Ontario, including traditional systems, Eljen GSF options, holding tanks, permits, and OBC-compliant installs.",
              areaServed: {
                "@type": "AdministrativeArea",
                name: "Thunder Bay & Northwestern Ontario",
              },
              provider: {
                "@type": "LocalBusiness",
                name: "Groundhogs Septic Systems",
                url: "https://groundhogsseptics.com/",
              },
              serviceType: [
                "Septic system installation",
                "Traditional leaching bed installation",
                "Eljen GSF system installation",
                "Holding tank installation",
              ],
            },
            {
              "@type": "FAQPage",
              mainEntity: systemInstallFaq.map((item) => ({
                "@type": "Question",
                name: item.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: item.answer,
                },
              })),
            },
          ],
        }
      : undefined;

  return (
    <div>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        structuredData={structuredData || serviceSchema(service.title, service.description)}
      />
      <Header />

      {/* Hero Section */}
      <section
        className="gh-intro gh-photo-intro pt-32 pb-16 relative"
        style={{
          backgroundImage: `url('${service.image}')`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div
          className="absolute inset-0"
          style={{ backgroundColor: "rgba(28, 43, 58, 0.85)" }}
        />
        <div className="gh-photo-intro-copy relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white" data-reveal>
          <Link
            to="/"
            className="inline-flex items-center space-x-2 mb-6 hover:opacity-70"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <h1 className="text-5xl font-bold mb-4">{displayTitle}</h1>
          <p className="text-xl max-w-3xl mx-auto">{service.description}</p>
        </div>
      </section>

      {/* Primary CTA Block - Estimate & Calculator */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl font-bold mb-4"
            style={{ color: "var(--dark-navy)" }}
          >
            Ready to Get a Septic Estimate?
          </h2>
          <p className="text-lg mb-8" style={{ color: "var(--text-light)" }}>
            Start with a free estimate form or use our septic cost calculator to see
            ballpark pricing for your property.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 rounded-md font-bold text-white hover:opacity-90"
              style={{ backgroundColor: "var(--primary-orange)" }}
            >
              Request Free Estimate
            </Link>
            <Link
              to="/septic-calculator"
              className="inline-flex items-center justify-center px-8 py-4 rounded-md font-bold border-2 hover:bg-orange-50"
              style={{
                borderColor: "var(--primary-orange)",
                color: "var(--primary-orange)"
              }}
            >
              Use Septic Cost Calculator
            </Link>
          </div>
        </div>
      </section>


      {/* Service Details - Render fullDescription if available, otherwise show default template */}
      {service.fullDescription ? (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="service-content"
              dangerouslySetInnerHTML={{
                __html: (() => {
                  if (!service.fullDescription) return "";

                  let html = service.fullDescription;

                  // Inject Thunder Bay-specific content above Traditional Leach Field Systems
                  if (slug === "septic-system-installation-thunder-bay") {
                    const marker =
                      '<h3 class="text-2xl font-bold mt-8 mb-4" style="color: var(--dark-navy);">Traditional Leach Field Systems</h3>';

                    const thunderBayBlock = `
    <h3 class="text-2xl font-bold mt-8 mb-4" style="color: var(--dark-navy);">Septic System Installation in Thunder Bay (Traditional, Eljen, Holding Tanks)</h3>
    <p class="text-gray-700">
        Groundhogs&trade; provides engineered septic system installation across Thunder Bay and Northwestern Ontario. With 1,200+ installs and 15+ years of experience, we build systems designed for clay soils, shallow bedrock, high water tables, and Northern Ontario freezethaw conditions.
    </p>
    <p class="text-gray-700">
        Homeowners searching for &quot;septic installation Thunder Bay&quot;, &quot;septic installers near me&quot;, or &quot;septic tanks Thunder Bay&quot; typically need one of three paths: a traditional leaching bed where soils allow, an Eljen GSF system for challenging sites, or a holding tank solution when a field is not viable.
    </p>
    <p class="text-gray-700">
        We coordinate permits/inspections and ensure the final install meets Ontario Building Code requirements and site constraints.
    </p>
    <p class="text-gray-700">
      Looking at poly/plastic tank options as part of your septic design? See our <a href="/services/roth-multitank-supplier-thunder-bay" class="text-orange-600 hover:underline">Roth MultiTank supply &amp; installation page</a>.
    </p>

    <div class="mt-8 p-6 rounded-lg" style="background-color: #f3f4f6;">
      <h3 class="text-xl font-bold mb-2" style="color: var(--dark-navy);">Next Steps (Thunder Bay)</h3>
      <ul class="list-disc pl-6 space-y-2">
        <li><a href="/services/septic-system-replacement-thunder-bay" class="text-orange-600 hover:underline">Septic System Replacement (Thunder Bay)</a></li>
        <li><a href="/services/septic-tank-installation-thunder-bay" class="text-orange-600 hover:underline">Septic Tank Installation &amp; Replacement (Thunder Bay)</a></li>
        <li><a href="/guides/percolation-test-thunder-bay-ontario" class="text-orange-600 hover:underline">Percolation Test (Thunder Bay / Ontario)</a></li>
        <li><a href="/guides/septic-system-replacement-cost-thunder-bay" class="text-orange-600 hover:underline">Septic Replacement Cost Guide (Thunder Bay)</a></li>
      </ul>
    </div>
`;

                    const index = html.indexOf(marker);
                    if (index !== -1) {
                      html =
                        html.slice(0, index) + thunderBayBlock + marker + html.slice(index + marker.length);
                    } else {
                      // Fallback: append at the end if marker not found
                      html = html + thunderBayBlock;
                    }
                  }

                  // Inject block above Cost Comparison on Septic Field Installation / Replacement page
                  if (slug === "septic-field-installation-replacement-thunder-bay") {
                    const marker =
                      '<h3 class="text-2xl font-bold mt-8 mb-4" style="color: var(--dark-navy);">Cost Comparison: Traditional vs. Eljen</h3>';

                    const fieldBlock = `
    <h3 class="text-2xl font-bold mt-8 mb-4" style="color: var(--dark-navy);">Engineered Septic Field Installation & Replacement Designed for Thunder Bay Conditions</h3>
    <p class="text-gray-700">
        A septic field must be designed specifically for the soil, drainage patterns, and terrain of Northwestern Ontario — and no two properties in Thunder Bay behave the same. Homeowners searching for "septic field replacement Thunder Bay," "septic field installation near me," or "septic field repair" rely on Groundhogs&trade; because we understand the unique challenges our region presents: clay-heavy soils, shallow bedrock, high water tables, sloped lots, and properties near lakes and rivers. These conditions require engineered solutions, not generic drain field construction. That’s why every system we install is designed to exceed OBC requirements and withstand decades of Northern Ontario freeze–thaw cycles.
    </p>
    <p class="text-gray-700">
        If your drain field is saturated, backing up, or no longer filtering properly, replacing it quickly is critical — but so is diagnosing why the failure occurred. Many older Thunder Bay systems were installed on inadequate soil, in incorrect locations, built too shallow or too deep, or designed without proper distribution. Our team performs a full site evaluation including soil structure, percolation performance, groundwater height, and available field area before recommending a new leach field or an Eljen GSF system. This diagnostic approach ensures your new septic field won’t just work — it will last.
    </p>
    <p class="text-gray-700">
        Thunder Bay’s mix of rural communities (Shuniah, Murillo, Rosslyn, Gorham, Nolalu, Pass Lake, Oliver Paipoonge, Kaministiquia) presents widely different soil profiles. Some lots need long trench-based traditional fields; others require compact Eljen fields that fit into tight spaces or poor soils. Whether we’re replacing a failed drain field or installing a brand new system, Groundhogs&trade; designs every layout for optimal drainage flow, reduced hydraulic loading, and long-term reliability.
    </p>
    <p class="text-gray-700">
        A properly engineered septic field is the heart of your system — and choosing the right installer makes the difference between a system that lasts 30+ years and one that fails prematurely. Groundhogs&trade; provides the highest level of field design, installation, and replacement in Thunder Bay, ensuring maximum lifespan, drainage performance, and environmental protection for your property.
    </p>
`;

                    const index = html.indexOf(marker);
                    if (index !== -1) {
                      html =
                        html.slice(0, index) + fieldBlock + marker + html.slice(index + marker.length);
                    } else {
                      html = html + fieldBlock;
                    }
                  }

                  // Inject SEO block into Septic Tank Installation Thunder Bay page (within HTML content)
                  if (slug === "septic-tank-installation-thunder-bay") {
                    const tankBlock = `
    <h3 class="text-2xl font-bold mt-8 mb-4" style="color: var(--dark-navy);">Engineered Septic Tank Installation Built for Thunder Bay Soil, Weather, and OBC Requirements</h3>
    <p class="text-gray-700">
        A septic tank is the foundation of your wastewater system, and in Northern Ontario it must be engineered to handle extreme weather,
        weather, high groundwater, and varied soil conditions. Homeowners searching for &quot;septic tank installation near me&quot;, &quot;septic
        tank replacement Thunder Bay&quot;, or &quot;septic tank installers in Thunder Bay&quot; choose Groundhogs&trade; because we design systems
        specifically for the region&apos;s clay-heavy soils, shallow bedrock, rugged terrain, and harsh freeze–thaw cycles. A properly
        installed tank ensures your septic field can operate efficiently for decades, while a poorly installed tank can lead to
        premature system failure, backups, and costly repairs.
    </p>
    <p class="text-gray-700">
        Many septic tanks in Thunder Bay were installed 25–40 years ago, long before today&apos;s OBC standards and engineering practices.
        Tanks from this era often have deteriorated baffles, structural cracks, improper elevations, missing risers, or undersized capacity
        for modern households. Groundhogs&trade; performs a full diagnostic evaluation — including tank integrity, inflow/outflow elevation,
        bedrock depth, seasonal groundwater, and overall system load — to determine the ideal tank size and configuration for your
        property.
    </p>
    <p class="text-gray-700">
        Thunder Bay&apos;s diverse landscapes—from Shuniah&apos;s rocky bedrock to Murillo&apos;s sandy soils to areas with high water tables along Lake
        Superior—require precise excavation and tank placement. We ensure proper bedding material, correct slope to the field, stabilized
        backfill, and watertight connections that prevent infiltration or exfiltration. Whether installing a concrete tank for maximum
        durability or a fiberglass tank for tight-access rural lots, every installation includes engineered elevations, secure pipe
        connections, risers to ground level, and complete OBC-compliant integration with your existing or new septic field.
    </p>
    <p class="text-gray-700">
        A septic tank is not something you replace often — it must be done right the first time. With 1,200+ successful installations across
        Northwestern Ontario, Groundhogs&trade; provides the most reliable and technically accurate septic tank installation available in the
        region. If you&apos;re noticing tank deterioration, sewage odors, backups, or if your tank is over 30 years old, our team can assess your
        system and recommend the safest, most cost-effective solution.
    </p>
`;

                    const firstParagraphEnd = html.indexOf('</p>');
                    if (firstParagraphEnd !== -1) {
                      html =
                        html.slice(0, firstParagraphEnd + 4) + tankBlock + html.slice(firstParagraphEnd + 4);
                    } else {
                      html = tankBlock + html;
                    }

                    const faqHtml = `
    <h3 class="text-2xl font-bold mt-10 mb-4" style="color: var(--dark-navy);">Septic Tank Installation & Replacement FAQs (Thunder Bay)</h3>
    <div class="space-y-4">
      <div>
        <h4 class="font-semibold text-lg mb-1" style="color: var(--dark-navy);">Do you do septic tank installation and replacement in Thunder Bay?</h4>
        <p class="text-gray-700">Yes. We install septic tanks for new builds and replace existing tanks that are cracked, undersized, leaking, or no longer code-compliant. We serve Thunder Bay and surrounding Northwestern Ontario communities.</p>
      </div>
      <div>
        <h4 class="font-semibold text-lg mb-1" style="color: var(--dark-navy);">Can you replace just the tank and keep the existing septic field?</h4>
        <p class="text-gray-700">Sometimes. If the drain field is still functioning, we may be able to reconnect a new tank to the existing field. We assess the field and explain your options clearly before any work begins.</p>
      </div>
      <div>
        <h4 class="font-semibold text-lg mb-1" style="color: var(--dark-navy);">Do you handle permits and inspections?</h4>
        <p class="text-gray-700">Yes. We coordinate required permits and inspections and install to Ontario Building Code requirements and local health unit processes.</p>
      </div>
      <div>
        <h4 class="font-semibold text-lg mb-1" style="color: var(--dark-navy);">What types of septic tanks do you install?</h4>
        <p class="text-gray-700">We install concrete and fiberglass tanks, and we can also supply and install poly/plastic tank options (including Roth MultiTank) where appropriate for your project.</p>
      </div>
      <div>
        <h4 class="font-semibold text-lg mb-1" style="color: var(--dark-navy);">How long does a septic tank replacement take?</h4>
        <p class="text-gray-700">Many tank replacements can be completed in about 3 days on average depending on access, soil conditions, and weather. We’ll confirm timelines after a site review.</p>
      </div>
    </div>
`;

                    if (!html.includes("Septic Tank Installation & Replacement FAQs")) {
                      html = html + faqHtml;
                    }
                  }

                  return html;
                })()
              }}
            />
          </div>
        </section>
      ) : (
        <>
          {service.title && service.title.includes("Septic Tank Installation") && (
            <section className="py-16 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2
                  className="text-3xl font-bold mb-6"
                  style={{ color: "var(--dark-navy)" }}
                >
                  Engineered Septic Tank Installation Built for Thunder Bay Soil, Weather, and OBC Requirements
                </h2>
                <div
                  className="space-y-4 text-lg"
                  style={{ color: "var(--text-light)" }}
                >
                  <p>
                    A septic tank is the foundation of your wastewater system, and in Northern Ontario it must be engineered to handle extreme
                    weather, high groundwater, and varied soil conditions. Homeowners searching for &quot;septic tank installation near me&quot;, &quot;septic
                    tank replacement Thunder Bay&quot;, or &quot;septic tank installers in Thunder Bay&quot; choose Groundhogs&trade; because we design systems
                    specifically for the region&apos;s clay-heavy soils, shallow bedrock, rugged terrain, and harsh freeze–thaw cycles. A properly
                    installed tank ensures your septic field can operate efficiently for decades, while a poorly installed tank can lead to
                    premature system failure, backups, and costly repairs.
                  </p>
                  <p>
                    Many septic tanks in Thunder Bay were installed 25–40 years ago, long before today&apos;s OBC standards and engineering
                    practices. Tanks from this era often have deteriorated baffles, structural cracks, improper elevations, missing risers, or
                    undersized capacity for modern households. Groundhogs&trade; performs a full diagnostic evaluation — including tank
                    integrity, inflow/outflow elevation, bedrock depth, seasonal groundwater, and overall system load — to determine the ideal
                    tank size and configuration for your property.
                  </p>
                  <p>
                    Thunder Bay&apos;s diverse landscapes—from Shuniah&apos;s rocky bedrock to Murillo&apos;s sandy soils to areas with high water tables
                    along Lake Superior—require precise excavation and tank placement. We ensure proper bedding material, correct slope to the
                    field, stabilized backfill, and watertight connections that prevent infiltration or exfiltration. Whether installing a
                    concrete tank for maximum durability or a fiberglass tank for tight-access rural lots, every installation includes
                    engineered elevations, secure pipe connections, risers to ground level, and complete OBC-compliant integration with your
                    existing or new septic field.
                  </p>
                  <p>
                    A septic tank is not something you replace often — it must be done right the first time. With 1,200+ successful
                    installations across Northwestern Ontario, Groundhogs&trade; provides the most reliable and technically accurate septic tank
                    installation available in the region. If you&apos;re noticing tank deterioration, sewage odors, backups, or if your tank is over
                    30 years old, our team can assess your system and recommend the safest, most cost-effective solution.
                  </p>
                </div>
              </div>
            </section>
          )}

          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <h2
                    className="text-3xl font-bold mb-6"
                    style={{ color: "var(--dark-navy)" }}
                  >
                    What&apos;s Included
                  </h2>
                  <ul className="space-y-4">
                    {service.features && service.features.length > 0 ? (
                      service.features.map((feature, index) => (
                        <li
                          key={index}
                          className="flex items-start space-x-3"
                        >
                          <CheckCircle
                            size={24}
                            style={{ color: "var(--primary-orange)" }}
                            className="flex-shrink-0 mt-1"
                          />
                          <span>{feature}</span>
                        </li>
                      ))
                    ) : (
                      <>
                        <li className="flex items-start space-x-3">
                          <CheckCircle
                            size={24}
                            style={{ color: "var(--primary-orange)" }}
                            className="flex-shrink-0 mt-1"
                          />
                          <div>
                            <strong>Complete Site Evaluation</strong>
                            <p
                              className="text-sm"
                              style={{ color: "var(--text-light)" }}
                            >
                              Thorough assessment of soil conditions, water table,
                              and site constraints
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle
                            size={24}
                            style={{ color: "var(--primary-orange)" }}
                            className="flex-shrink-0 mt-1"
                          />
                          <div>
                            <strong>Permit Management</strong>
                            <p
                              className="text-sm"
                              style={{ color: "var(--text-light)" }}
                            >
                              We handle all applications, approvals, and
                              inspections
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle
                            size={24}
                            style={{ color: "var(--primary-orange)" }}
                            className="flex-shrink-0 mt-1"
                          />
                          <div>
                            <strong>Professional Excavation</strong>
                            <p
                              className="text-sm"
                              style={{ color: "var(--text-light)" }}
                            >
                              Precise excavation with minimal property
                              disruption
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle
                            size={24}
                            style={{ color: "var(--primary-orange)" }}
                            className="flex-shrink-0 mt-1"
                          />
                          <div>
                            <strong>Expert Installation</strong>
                            <p
                              className="text-sm"
                              style={{ color: "var(--text-light)" }}
                            >
                              Code-compliant installation by certified
                              technicians
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle
                            size={24}
                            style={{ color: "var(--primary-orange)" }}
                            className="flex-shrink-0 mt-1"
                          />
                          <div>
                            <strong>Final Grading & Restoration</strong>
                            <p
                              className="text-sm"
                              style={{ color: "var(--text-light)" }}
                            >
                              Clean, professional site restoration and
                              landscaping
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle
                            size={24}
                            style={{ color: "var(--primary-orange)" }}
                            className="flex-shrink-0 mt-1"
                          />
                          <div>
                            <strong>5-Year Warranty</strong>
                            <p
                              className="text-sm"
                              style={{ color: "var(--text-light)" }}
                            >
                              Comprehensive warranty on all installations
                            </p>
                          </div>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
                <div>
                  <h2
                    className="text-3xl font-bold mb-6"
                    style={{ color: "var(--dark-navy)" }}
                  >
                    The Groundhogs Advantage
                  </h2>
                  <div className="space-y-6">
                    <div
                      className="p-6 rounded-lg"
                      style={{ backgroundColor: "var(--light-gray)" }}
                    >
                      <h3
                        className="text-xl font-bold mb-2"
                        style={{ color: "var(--dark-navy)" }}
                      >
                        Northwestern Ontario Experts
                      </h3>
                      <p style={{ color: "var(--text-light)" }}>
                        Over 15 years of experience with the region&apos;s unique soil
                        conditions, climate challenges, and regulatory
                        requirements.
                      </p>
                      <p
                        className="mt-3 text-sm"
                        style={{ color: "var(--text-light)" }}
                      >
                        Learn more about our process in our
                        <Link
                          to="/blog/systemized-septic-installation-approach-groundhogs"
                          className="ml-1 text-orange-600 hover:underline"
                        >
                          systemized septic installation guide
                        </Link>
                        .
                      </p>
                    </div>
                    <div
                      className="p-6 rounded-lg"
                      style={{ backgroundColor: "var(--light-gray)" }}
                    >
                      <h3
                        className="text-xl font-bold mb-2"
                        style={{ color: "var(--dark-navy)" }}
                      >
                        Eljen Certified Installers
                      </h3>
                      <p style={{ color: "var(--text-light)" }}>
                        Factory-trained and certified to install advanced Eljen
                        mound systems to manufacturer specifications.
                      </p>
                      <p
                        className="mt-3 text-sm"
                        style={{ color: "var(--text-light)" }}
                      >
                        For a deeper dive into system options, read our
                        <Link
                          to="/blog/traditional-vs-eljen-septic-systems-thunder-bay"
                          className="ml-1 text-orange-600 hover:underline"
                        >
                          traditional vs. Eljen septic systems guide
                        </Link>
                        .
                      </p>
                    </div>
                    <div
                      className="p-6 rounded-lg"
                      style={{ backgroundColor: "var(--light-gray)" }}
                    >
                      <h3
                        className="text-xl font-bold mb-2"
                        style={{ color: "var(--dark-navy)" }}
                      >
                        Efficient, Clean Work
                      </h3>
                      <p style={{ color: "var(--text-light)" }}>
                        Our experienced crews complete installations efficiently
                        while maintaining clean, organized worksites.
                      </p>
                      <p
                        className="mt-3 text-sm"
                        style={{ color: "var(--text-light)" }}
                      >
                        Discover how our crews complete most installations in
                        just a few days in our
                        <Link
                          to="/blog/fast-septic-installation-1-3-days-thunder-bay"
                          className="ml-1 text-orange-600 hover:underline"
                        >
                          fast septic installation article
                        </Link>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section
            className="py-16"
            style={{ backgroundColor: "var(--light-gray)" }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2
                className="text-4xl font-bold mb-12 text-center"
                style={{ color: "var(--dark-navy)" }}
              >
                Our Installation Process
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
                    style={{ backgroundColor: "var(--primary-orange)" }}
                  >
                    1
                  </div>
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ color: "var(--dark-navy)" }}
                  >
                    Site Evaluation
                  </h3>
                  <p style={{ color: "var(--text-light)" }}>
                    Comprehensive assessment and system design
                  </p>
                </div>
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
                    style={{ backgroundColor: "var(--primary-orange)" }}
                  >
                    2
                  </div>
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ color: "var(--dark-navy)" }}
                  >
                    Permits & Approvals
                  </h3>
                  <p style={{ color: "var(--text-light)" }}>
                    We handle all paperwork and inspections
                  </p>
                </div>
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
                    style={{ backgroundColor: "var(--primary-orange)" }}
                  >
                    3
                  </div>
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ color: "var(--dark-navy)" }}
                  >
                    Installation
                  </h3>
                  <p style={{ color: "var(--text-light)" }}>
                    Expert installation to code standards
                  </p>
                </div>
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
                    style={{ backgroundColor: "var(--primary-orange)" }}
                  >
                    4
                  </div>
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ color: "var(--dark-navy)" }}
                  >
                    Final Inspection
                  </h3>
                  <p style={{ color: "var(--text-light)" }}>
                    Complete testing and site restoration
                  </p>
                </div>
              </div>
            </div>
          </section>
      {/* Guides & Cost Resources - Internal SEO Block */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl font-bold mb-4"
            style={{ color: "var(--dark-navy)" }}
          >
            Guides &amp; Cost Resources for Ontario Homeowners
          </h2>
          <p className="mb-4" style={{ color: "var(--text-light)" }}>
            If you are still planning your project or comparing options, these in-depth
            guides explain real-world septic system costs, percolation testing, and
            advanced Eljen systems for clay, bedrock, and high-water-table sites in
            Thunder Bay and across Ontario.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-orange-700">
            <li>
              <Link to="/guides/septic-system-cost-ontario" className="hover:underline">
                Septic System Cost Ontario (2025 Guide)
              </Link>
            </li>
            <li>
              <Link
                to="/guides/septic-tank-installation-cost-ontario"
                className="hover:underline"
              >
                Septic Tank Installation Cost Ontario (2025 Guide)
              </Link>
            </li>
            <li>
              <Link
                to="/guides/percolation-test-thunder-bay-ontario"
                className="hover:underline"
              >
                Percolation Test Thunder Bay / Ontario (2025 Guide)
              </Link>
            </li>
            <li>
              <Link to="/guides/eljen-gsf-system-ontario" className="hover:underline">
                Eljen GSF System – Ontario Guide (2025 Advanced Treatment Overview)
              </Link>
            </li>
          </ul>
        </div>
      </section>

        </>
      )}

      {/* Service Areas Section */}
      {/* Service-specific FAQ sections */}
      {slug === "septic-system-installation-thunder-bay" && (
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              className="text-3xl font-bold mb-6"
              style={{ color: "var(--dark-navy)" }}
            >
              Frequently Asked Questions
            </h2>
            <div className="space-y-6" style={{ color: "var(--text-light)" }}>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: How long does a septic system installation take in Thunder Bay?
                </h3>
                <p>
                  A: Most installations take about 3 days on average depending on soil conditions, access, system type, and groundwater
                  levels. Thunder Bay’s clay soils and shallow bedrock can add complexity, but our team completes installs
                  faster than most contractors.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Do you handle all permits and engineering?
                </h3>
                <p>
                  A: Yes. Groundhogs manages all OBC permit applications, engineering requirements, soil testing, and
                  layout design. We maintain a 100% permit approval rate across Northwestern Ontario.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Should I choose a traditional system or an Eljen GSF system?
                </h3>
                <p>
                  A: It depends on your soil, lot size, and groundwater elevation. Traditional systems work best in
                  sandier soils, while Eljen GSF systems perform extremely well in clay, bedrock, and high-water-table
                  areas common around Thunder Bay.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Can you install septic systems year-round?
                </h3>
                <p>
                  A: Yes. Winter installations are possible with proper preparation, though frozen ground may affect
                  excavation depth and schedule. Contact us to confirm seasonal availability.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: How much does a septic system cost in Thunder Bay?
                </h3>
                <p>
                  A: Costs vary based on soil, system type, and field size. Most range from $15,000–$30,000. For exact
                  pricing, use our Septic Cost Calculator or request a detailed estimate.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {slug === "septic-field-installation-replacement-thunder-bay" && (
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              className="text-3xl font-bold mb-6"
              style={{ color: "var(--dark-navy)" }}
            >
              Frequently Asked Questions
            </h2>
            <div className="space-y-6" style={{ color: "var(--text-light)" }}>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: What causes septic field failure in Thunder Bay?
                </h3>
                <p>
                  A: Common causes include saturated clay soil, bedrock interference, high groundwater, aging drain
                  lines, improper installation, and excess water loading. Freeze–thaw cycles can also damage
                  distribution lines.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Can my existing septic tank be reused when replacing the field?
                </h3>
                <p>
                  A: Often yes. If the tank is structurally sound and meets current code requirements, we can reconnect
                  the new field to your existing tank, saving thousands in replacement costs.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: How long does a septic field replacement take?
                </h3>
                <p>
                  A: Most replacements take 2–4 days depending on site access, excavation complexity, and whether a
                  traditional or Eljen system is being installed.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Is Eljen GSF better for replacement fields?
                </h3>
                <p>
                  A: In many Thunder Bay and rural Northwestern Ontario properties, yes. Eljen systems work exceptionally
                  well in clay and poor-draining soils and require 70% less field area.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: What are signs that my drain field is failing?
                </h3>
                <p>
                  A: Symptoms include soggy ground, sewage odors, slow drains, backups, unusually green grass, or the
                  tank filling faster than normal. These issues should be inspected immediately.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {slug === "septic-tank-installation-thunder-bay" && (
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              className="text-3xl font-bold mb-6"
              style={{ color: "var(--dark-navy)" }}
            >
              Frequently Asked Questions
            </h2>
            <div className="space-y-6" style={{ color: "var(--text-light)" }}>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: How do I know if my septic tank needs to be replaced?
                </h3>
                <p>
                  A: Indicators include cracks, structural deterioration, failing baffles, groundwater infiltration,
                  insufficient capacity, or tanks more than 30–40 years old. We inspect and confirm whether replacement
                  is necessary.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Should I install a concrete or fiberglass tank?
                </h3>
                <p>
                  A: Concrete tanks are extremely durable and ideal for clay or high-water-table areas. Fiberglass tanks
                  work well on difficult-access lots or properties with bedrock. We recommend the best option for your
                  site.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: How long does tank replacement take?
                </h3>
                <p>
                  A: Most replacements are completed in about 3 days on average, including excavation, tank removal, new tank
                  installation, backfilling, and site restoration.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Can my existing septic field be reused with a new tank?
                </h3>
                <p>
                  A: Yes — if your field is still functioning properly. We assess field condition during inspection and
                  let you know if reconnection is possible.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Do you handle all permits for septic tank installation?
                </h3>
                <p>
                  A: Yes. Groundhogs manages all OBC permit paperwork, inspections, and approvals to ensure your system
                  is fully compliant.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {slug === "septic-repair-near-me" && (
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              className="text-3xl font-bold mb-6"
              style={{ color: "var(--dark-navy)" }}
            >
              Frequently Asked Questions
            </h2>
            <div className="space-y-6" style={{ color: "var(--text-light)" }}>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: What are the most common septic problems in Thunder Bay?
                </h3>
                <p>
                  A: Frequent issues include saturated drain fields, frozen lines, pump failures, clogged pipes,
                  collapsed distribution boxes, and groundwater infiltration — all made worse by Northern Ontario’s soil
                  and climate.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Do you offer emergency septic repair?
                </h3>
                <p>
                  A: Yes. We provide 24/7 emergency service for sewage backups, pump failures, overflowing tanks, and
                  field flooding. Immediate help is available by calling 1-888-907-3777.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: How quickly can you respond to a septic emergency?
                </h3>
                <p>
                  A: In many Thunder Bay areas we offer same-day response. Rural locations are typically next-day
                  depending on weather and road access.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Do I need my system pumped before a repair?
                </h3>
                <p>
                  A: Not always. Pumping is sometimes unnecessary and may not fix the root problem. We diagnose the
                  actual cause first to save you time and money.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: What should I do if sewage is backing up into my home?
                </h3>
                <p>
                  A: Stop all water use immediately and call for emergency service. Backups indicate a critical
                  blockage or field failure and should be addressed right away.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}



      <section
        className="py-16"
        style={{ backgroundColor: "var(--light-gray)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl font-bold mb-4 text-center"
            style={{ color: "var(--dark-navy)" }}
          >
            Available Throughout Northwestern Ontario
          </h2>
          <p
            className="text-center mb-8 text-lg"
            style={{ color: "var(--text-light)" }}
          >
            We provide {service?.title || "septic services"} across the region
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Link
              to="/service-areas/thunder-bay"
              className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
            >
              <h3
                className="font-semibold"
                style={{ color: "var(--dark-navy)" }}
              >
                Thunder Bay
              </h3>
            </Link>
            <Link
              to="/service-areas/kenora"
              className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
            >
              <h3
                className="font-semibold"
                style={{ color: "var(--dark-navy)" }}
              >
                Kenora
              </h3>
            </Link>
            <Link
              to="/service-areas/dryden"
              className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
            >
              <h3
                className="font-semibold"
                style={{ color: "var(--dark-navy)" }}
              >
                Dryden
              </h3>
            </Link>
            <Link
              to="/service-areas/fort-frances"
              className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
            >
              <h3
                className="font-semibold"
                style={{ color: "var(--dark-navy)" }}
              >
                Fort Frances
              </h3>
            </Link>
            <Link
              to="/service-areas/sioux-lookout"
              className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
            >
              <h3
                className="font-semibold"
                style={{ color: "var(--dark-navy)" }}
              >
                Sioux Lookout
              </h3>
            </Link>
            <Link
              to="/service-areas/atikokan"
              className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
            >
              <h3
                className="font-semibold"
                style={{ color: "var(--dark-navy)" }}
              >
                Atikokan
              </h3>
            </Link>
            <Link
              to="/service-areas/nipigon"
              className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
            >
              <h3
                className="font-semibold"
                style={{ color: "var(--dark-navy)" }}
              >
                Nipigon
              </h3>
            </Link>
            <Link
              to="/service-areas/red-lake"
              className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
            >
              <h3
                className="font-semibold"
                style={{ color: "var(--dark-navy)" }}
              >
                Red Lake
              </h3>
            </Link>
            <Link
              to="/service-areas/marathon"
              className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
            >
              <h3
                className="font-semibold"
                style={{ color: "var(--dark-navy)" }}
              >
                Marathon
              </h3>
            </Link>
            <Link
              to="/service-areas/terrace-bay"
              className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
            >
              <h3
                className="font-semibold"
                style={{ color: "var(--dark-navy)" }}
              >
                Terrace Bay
              </h3>
            </Link>
            <Link
              to="/service-areas/schreiber"
              className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
            >
              <h3
                className="font-semibold"
                style={{ color: "var(--dark-navy)" }}
              >
                Schreiber
              </h3>
            </Link>
            <Link
              to="/service-areas/ignace"
              className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
            >
              <h3
                className="font-semibold"
                style={{ color: "var(--dark-navy)" }}
              >
                Ignace
              </h3>
            </Link>
          </div>
          <div className="text-center mt-8">
            <Link
              to="/service-areas"
              className="inline-block px-6 py-3 rounded-md font-semibold text-white hover:opacity-90"
              style={{ backgroundColor: "var(--primary-orange)" }}
            >
              View All Service Areas →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-4xl font-bold mb-4"
            style={{ color: "var(--dark-navy)" }}
          >
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8" style={{ color: "var(--text-light)" }}>
            Contact us today for a free virtual consultation and detailed estimate.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center">
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center space-x-2 px-8 py-4 rounded-md font-bold text-white hover:opacity-90"
              style={{ backgroundColor: "var(--primary-orange)" }}
            >
              <Phone size={24} />
              <span>{companyInfo.phone}</span>
            </a>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 rounded-md font-bold border-2 hover:bg-orange-50"
              style={{
                borderColor: "var(--primary-orange)",
                color: "var(--primary-orange)"
              }}
            >
              Request Free Estimate
            </Link>
            <Link
              to="/septic-calculator"
              className="inline-block px-8 py-4 rounded-md font-bold border-2 hover:bg-orange-50"
              style={{
                borderColor: "var(--primary-orange)",
                color: "var(--primary-orange)"
              }}
            >
              View Septic Cost Calculator
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePage;
